import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { isEmpty } from 'ramda'
import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom-v5-compat'

import Loading from '@/app/component/guard/loading'
import { generateError, processContactLabel } from '@/app/module/cati/helpers'
import { CATIType, CampaignType, RunType, SystemErrorType } from '@/app/module/cati/types'

import CampaignHeader from './campaign-header'
import CATIControls from './cati-controls'
import CATIStepCard from './cati-step-card'
import ContactDetails from './contact-details'
import CATIErrorLayout from './error'
import StatusModal from './status-modal'
import SurveyCompleted from './survey-completed'

export type Props = {
  campaign: CampaignType
  contact: RunType
  data: CATIType
  done: boolean
  error: SystemErrorType | null
  itemId: string
  loading: boolean
  orgId: number
  runId: string
  token: string
  closeCATISurvey: (payload: { token: string; orgId: number; itemId: string; runId: string }) => Promise<any>
  getCATIItem: (payload: { token: string; orgId: number; itemId: string; runId: string }) => void
  getCATIStep: (payload: {
    token: string
    orgId: number
    itemId: string
    runId: string
    stepId: string | undefined
    answer: string
    answers?: string[]
  }) => Promise<any>
  getSubDetails: (payload: { token: string; orgId: number; subId: string }) => void
  setSubStatus: (payload: { token: string; orgId: number; subId: string; status: string }) => Promise<void>
  toggleCallDialog: (payload: {
    autoStart?: boolean
    contactId?: number
    contactName?: string
    subId?: string
    isOpen: boolean
  }) => void
}

type CATISurveyLocationState = {
  autoStartCall?: boolean
}

export default function CATIItem(props: Props) {
  const {
    token,
    orgId,
    itemId,
    runId,
    error,
    getCATIItem,
    getCATIStep,
    loading,
    data,
    campaign,
    toggleCallDialog,
    contact,
    done,
    getSubDetails,
    setSubStatus,
    closeCATISurvey,
  } = props
  const location = useLocation()
  const navigate = useNavigate()
  const autoStartCall = Boolean((location.state as CATISurveyLocationState | null)?.autoStartCall)
  const isCurrentRunLoaded = String(data?.id ?? '') === runId

  const exitButtonGroupRef = React.useRef(null)
  const autoStartedCallRunIdRef = React.useRef<string | null>(null)
  const [exitButtonOpen, setExitButtonOpen] = React.useState(false)
  const [initialized, setInitialized] = React.useState(false)
  const [showStatusModal, setShowStatusModal] = React.useState<null | 'update' | 'save' | 'exit'>(null)
  const [selectedAnswers, setSelectedAnswers] = React.useState<string[]>([])

  const selectedAnswer = selectedAnswers?.[0] || ''

  const handleToggle = () => {
    setExitButtonOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: any) => {
    if (exitButtonGroupRef.current && (exitButtonGroupRef.current as any).contains(event.target)) {
      return
    }
    setExitButtonOpen(false)
  }

  const handleMenuItemClick = (status: 'save' | 'exit') => {
    if (campaign.userStatuses && campaign.userStatuses.length > 0) {
      setShowStatusModal(status)
    } else if (status === 'save') {
      postStatusUpdateAction(status)
    } else if (status === 'exit') {
      postStatusUpdateAction(status)
    }
    setExitButtonOpen(false)
  }

  React.useEffect(() => {
    if (token && orgId && itemId && runId) {
      getCATIItem({ token, orgId, itemId, runId })
      getSubDetails({ token, orgId, subId: runId })
      setInitialized(true)
    }
  }, [itemId, orgId, runId, token, getCATIItem, getSubDetails])

  React.useEffect(() => {
    if (!autoStartCall) {
      autoStartedCallRunIdRef.current = null
    }
  }, [autoStartCall])

  const closeCallDialog = () => {
    toggleCallDialog({
      autoStart: false,
      isOpen: false,
      contactId: undefined,
      contactName: undefined,
      subId: undefined,
    })
  }

  const postStatusUpdateAction = (action: string) => {
    if (action === 'save') {
      navigate('/cati/contacts')
      closeCallDialog()
    } else if (action === 'exit') {
      closeCallDialog()
      closeCATISurvey({ token, orgId, itemId, runId }).then(() => {
        navigate('/cati/contacts')
      })
    }
  }

  const handleCompleteStep = () => {
    const basePayload = {
      token,
      orgId,
      itemId,
      runId,
      stepId: data.state.next?.id,
    }
    let promise: Promise<void | Error>
    if (!data.state.next?.multiSelect) {
      promise = getCATIStep({
        ...basePayload,
        answer: selectedAnswer,
      })
    } else {
      promise = getCATIStep({
        ...basePayload,
        answer: '',
        answers: selectedAnswers,
      })
    }

    promise.then((res: any) => {
      if (!(res instanceof Error)) {
        setSelectedAnswers([])
      }
    })
  }

  const openCallDialog = React.useCallback(
    (autoStart = false) => {
      if (!contact.id) {
        return
      }

      toggleCallDialog({
        autoStart,
        contactId: contact.id,
        contactName: processContactLabel(contact) || `contact (${contact.id})`,
        subId: runId,
        isOpen: true,
      })
    },
    [contact, runId, toggleCallDialog],
  )

  React.useEffect(() => {
    if (!autoStartCall || autoStartedCallRunIdRef.current === runId || !contact.id || !isCurrentRunLoaded) {
      return
    }

    autoStartedCallRunIdRef.current = runId
    openCallDialog(true)
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} })
  }, [
    autoStartCall,
    contact.id,
    isCurrentRunLoaded,
    location.pathname,
    location.search,
    navigate,
    openCallDialog,
    runId,
  ])

  const handleCallContact = () => {
    openCallDialog()
  }

  const processedError = generateError(campaign, done, () => navigate('/cati/contacts'), error)
  const contactLabel = processContactLabel(contact)

  const hasError = !isEmpty(processedError) && initialized && (campaign.template || !isEmpty(error))
  const isCompleted = data?.state && !data.state.next

  return (
    <Loading isLoading={loading || !initialized}>
      {showStatusModal && (
        <StatusModal
          isClose={showStatusModal === 'exit'}
          onCancel={() => setShowStatusModal(null)}
          onConfirm={(updatedStatus: string) => {
            setSubStatus({ token, orgId, subId: runId, status: updatedStatus }).then(() => {
              postStatusUpdateAction(showStatusModal)
              setShowStatusModal(null)
            })
          }}
          onClose={() => {
            postStatusUpdateAction(showStatusModal)
            setShowStatusModal(null)
          }}
          userStatuses={campaign.userStatuses}
          userStatus={data.userStatus}
        />
      )}
      <PageContainer>
        {hasError && <CATIErrorLayout {...processedError} />}
        {!hasError && (
          <Container>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', paddingBottom: 2 }}>
              <HeaderContainer>
                <CampaignHeader
                  campaignName={campaign.name}
                  userStatuses={campaign.userStatuses}
                  userStatus={data.userStatus}
                  onEditStatus={() => setShowStatusModal('update')}
                />
                {contact.id && <ContactDetails contactLabel={contactLabel} onCallContact={handleCallContact} />}
              </HeaderContainer>
              {isCompleted && <SurveyCompleted onMenuItemClick={handleMenuItemClick} />}
              {!isCompleted && (
                <>
                  {data && data.state && data.state.next && (
                    <CATIStepCard
                      message={data.state.next.message}
                      multiSelect={data.state.next.multiSelect}
                      openEnded={data.state.next.openEnded}
                      selectedAnswers={selectedAnswers}
                      stepNumber={(data.state.previous?.length || 0) + 1}
                      type={data.state.next.type}
                      validAnswers={data.state.next.validAnswers}
                      onAnswersChange={setSelectedAnswers}
                    />
                  )}
                  {data && data.state && data.state.next && (
                    <CATIControls
                      isQuestionType={data.state.next.type === 'catiQuestion'}
                      selectedAnswer={selectedAnswer}
                      exitButtonOpen={exitButtonOpen}
                      exitButtonGroupRef={exitButtonGroupRef}
                      onToggle={handleToggle}
                      onClose={handleClose}
                      onMenuItemClick={handleMenuItemClick}
                      onCompleteStep={handleCompleteStep}
                    />
                  )}
                </>
              )}
            </Box>
          </Container>
        )}
      </PageContainer>
    </Loading>
  )
}

const PageContainer = styled('div')({
  maxWidth: '1120px',
  width: '100%',
  margin: 'auto',
  padding: '1.5rem 1rem',
  height: 'calc(100vh - 64px)',
})

const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',

  [theme.breakpoints.down('sm')]: {
    gap: 16,
    flexDirection: 'column',

    '& > div': {
      maxWidth: '100%',
      width: '100%',
    },
  },
}))

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  gap: 16,
  justifyContent: 'space-between',
  paddingBottom: 32, // to avoid the support chat bubble
})
